/**
 * Rebate campaign-specific rules sections
 * Shows how the rebate works, the cashback levels, and a worked example
 * Rendered by the shared RulesTab shell
 */

import { useMemo, type FC } from 'react';

import { REBATE_CAMPAIGN_TYPE, useAutoJoin, type IRebateCampaign } from 'features/campaigns';
import TransWithFallback from 'shared/components/Translations/TransWrapper';
import { formatCurrencyValue, formatNumberWithSpaces } from 'shared/utils/helpers';
import type { IRulesContentProps } from '../../../../../shared/tabs/RulesTab/RulesTab';

import styles from './RebateRulesContent.module.css';

export const RebateRulesContent: FC<IRulesContentProps> = ({ campaign }) => {
  const rebate = campaign as IRebateCampaign;
  const autoJoin = useAutoJoin(REBATE_CAMPAIGN_TYPE);

  const levels = useMemo(
    () => [...(rebate.cashbackLevels ?? [])].sort((a, b) => a.levelNumber - b.levelNumber),
    [rebate.cashbackLevels],
  );

  // The example assumes the player stakes up to the top level's ceiling, so each
  // level's portion is the gap between consecutive ceilings.
  const totalStake = levels.length > 0 ? levels[levels.length - 1].betRangeTo : 0;

  const getPortionKey = (index: number): string => {
    if (index === levels.length - 1) return 'rebate.rules.example_portion_remaining';
    if (index === 0) return 'rebate.rules.example_portion_first';

    return 'rebate.rules.example_portion_next';
  };

  return (
    <>
      {/* General Information */}
      <div className={styles.section}>
        <div className={styles.title}>
          <TransWithFallback i18nKey="campaigns.general_information" />
        </div>
        <div>
          <TransWithFallback i18nKey="rebate.rules.general_info" />
        </div>
      </div>

      {/* Participation Rules */}
      <div className={styles.section}>
        <div className={styles.title}>
          <TransWithFallback i18nKey="campaigns.participation_rules" />
        </div>
        <ul className={styles.list}>
          {rebate.minStake != null && (
            <li className={styles.listItem}>
              <TransWithFallback i18nKey="rebate.rules.min_stake_amount" />:{' '}
              <span className={styles.highlightedText}>
                {formatCurrencyValue(rebate.minStake, 100)} {rebate.baseCurrency}
              </span>
            </li>
          )}
          {!autoJoin && (
            <li className={styles.listItem}>
              <TransWithFallback
                i18nKey="rebate.rules.join_rebate_text"
                components={{ b: <span className={styles.highlightedText} /> }}
              />
            </li>
          )}
        </ul>
      </div>

      {/* How Rebate Works */}
      <div className={styles.section}>
        <div className={styles.title}>
          <TransWithFallback i18nKey="rebate.rules.how_rebate_works" />
        </div>
        <div>
          <TransWithFallback i18nKey="rebate.rules.how_rebate_works_text" />
        </div>
      </div>

      {/* Rebate Levels */}
      <div className={styles.section}>
        <div className={styles.title}>
          <TransWithFallback i18nKey="rebate.rules.rebate_levels" />
        </div>
        <div>
          {/* Remove this piece of code, because minPayoutAmount is not needed anymore */}
          {/* {t('rebate.rules.min_payout_amount', 'Minimum payout amount')}:{' '}
          <span className={styles.highlightedText}>
            {formatNumberWithSpaces(rebate.minPayoutAmount)} {rebate.baseCurrency}
          </span> */}
          <TransWithFallback i18nKey="rebate.rules.max_payout_amount" />:{' '}
          <span className={styles.highlightedText}>
            {formatNumberWithSpaces(rebate.maxPayoutAmount)} {rebate.baseCurrency}
          </span>
        </div>
        <ul className={styles.list}>
          {levels.map((level) => (
            <li key={level.levelNumber} className={styles.listItem}>
              <TransWithFallback
                i18nKey="rebate.rules.level_range"
                components={{
                  b: <span className={styles.emphasis} />,
                  span: <span className={styles.highlightedText} />,
                }}
                values={{
                  number: level.levelNumber,
                  minAmount: formatNumberWithSpaces(level.betRangeFrom),
                  maxAmount: formatNumberWithSpaces(level.betRangeTo),
                  currency: rebate.baseCurrency,
                  percent: level.rebatePercent,
                }}
              />
            </li>
          ))}
        </ul>
      </div>

      {/* Example */}
      <div className={styles.section}>
        <div className={styles.title}>
          <TransWithFallback i18nKey="rebate.rules.example" />
        </div>
        <div>
          <TransWithFallback
            i18nKey="rebate.rules.example_intro"
            values={{
              amount: formatNumberWithSpaces(totalStake),
              currency: rebate.baseCurrency,
            }}
          />
        </div>
        <ul className={styles.list}>
          {levels.map((level, index) => {
            const portionStake = level.betRangeTo - (levels[index - 1]?.betRangeTo ?? 0);

            return (
              <li key={level.levelNumber} className={styles.listItem}>
                <TransWithFallback
                  i18nKey={getPortionKey(index)}
                  values={{
                    amount: formatNumberWithSpaces(portionStake),
                    currency: rebate.baseCurrency,
                  }}
                />
                {` → ${level.rebatePercent}% = ${formatNumberWithSpaces(level.maxPayoutAmount)} ${rebate.baseCurrency}`}
              </li>
            );
          })}
        </ul>
        <div>
          <TransWithFallback i18nKey="rebate.rules.total_rebate_earned" />:{' '}
          <span className={styles.highlightedText}>
            {formatNumberWithSpaces(rebate.maxPayoutAmount)} {rebate.baseCurrency}
          </span>
        </div>
      </div>
    </>
  );
};
